import React, { useEffect, useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import Modal, { ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import StepProgress from '../../components/StepProgress/StepProgress';
import EnrollPictureScreen from '../../components/EnrollPictureScreen/EnrollPictureScreen';
import { useToast } from '../../components/Toast/ToastContext';
import {
  trackGwaEnrolled,
  trackGwaEnrollmentFailed,
  trackGwaEnrollmentModalDismissed,
  trackGwaEnrollmentModalViewed,
} from '../../analytics';
import gwaHero from '../../assets/gwa-hero.jpg';
import sharedStyles from '../../components/EnrollPictureScreen/EnrollPictureScreen.module.scss';
import styles from './GwaEnrollModal.module.scss';
import { SUPPORT_EMAIL } from '@/rescue/lib/emails';

const ENROLL_GWA_MUTATION = gql`
  mutation RESCUE_enrollGwa($input: EnrollGwaInput!) {
    rescueEnrollGwa(input: $input) {
      enrollment {
        id
        slug
        promoCodeAlias
        pdpUrl
        active
      }
    }
  }
`;

interface GwaEnrollModalProps {
  open: boolean;
  onClose: () => void;
  onEnrolled: () => void;
}

export default function GwaEnrollModal({ open, onClose, onEnrolled }: GwaEnrollModalProps) {
  const [step, setStep] = useState(1);
  const [promoCode, setPromoCode] = useState('');
  const [error, setError] = useState('');
  const { showToast } = useToast();
  const [enrollGwa, { loading }] = useMutation(ENROLL_GWA_MUTATION);

  useEffect(() => {
    if (open) {
      trackGwaEnrollmentModalViewed();
    }
  }, [open]);

  const handleEnroll = async () => {
    setError('');
    const trimmed = promoCode.trim().toUpperCase();
    if (trimmed.length < 3 || trimmed.length > 20) {
      const msg = 'Code must be 3-20 characters';
      setError(msg);
      trackGwaEnrollmentFailed(msg, 'client', trimmed);
      return;
    }
    if (!/^[A-Z0-9-]+$/.test(trimmed)) {
      const msg = 'Only letters, numbers, and hyphens allowed';
      setError(msg);
      trackGwaEnrollmentFailed(msg, 'client', trimmed);
      return;
    }

    try {
      await enrollGwa({ variables: { input: { promoCodeAlias: trimmed } } });
      trackGwaEnrolled(trimmed);
      showToast('Your GWA program is live!');
      onEnrolled();
    } catch (err: any) {
      const msg = err?.graphQLErrors?.[0]?.message || err?.message || 'Something went wrong';
      setError(msg);
      trackGwaEnrollmentFailed(msg, 'server', trimmed);
    }
  };

  const handleClose = () => {
    trackGwaEnrollmentModalDismissed(step);
    setStep(1);
    setPromoCode('');
    setError('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="extra-wide"
      className={sharedStyles.modal}
      backdropClassName={sharedStyles.backdrop}
      showCloseX={false}
    >
      {step === 1 && (
        <EnrollPictureScreen
          heroSrc={gwaHero}
          onClose={handleClose}
          title="Offer adopters a free Fi Mini"
          description={
            <>
              Adopters who don't leave with a device get 6 months free via your rescue's branded landing page. Share
              with your trusted adopters, not on social media.
              <span className={styles.feeNote}>A one-time $20 activation fee applies.</span>
            </>
          }
          currentStep={1}
          totalSteps={2}
          actions={
            <Button variant="dark" onClick={() => setStep(2)}>
              Enroll &rarr;
            </Button>
          }
        />
      )}

      {step === 2 && (
        <>
          <button
            type="button"
            className={`${sharedStyles.closeX} ${styles.closeXDark}`}
            onClick={handleClose}
            aria-label="Close"
          >
            &times;
          </button>
          <div className={styles.bodyCenter}>
            <div className={styles.bodyHeader}>
              <h3 className={sharedStyles.title}>Set up your promo code</h3>
              <p className={sharedStyles.description}>
                Pick a code your adopters will use at checkout. You can change it anytime — only one code is active at a
                time.
              </p>
            </div>

            <div className={styles.bodyForm}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Your promo code</label>
                <div className={styles.fieldRow}>
                  <input
                    type="text"
                    className={styles.fieldInput}
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      setError('');
                    }}
                    placeholder="e.g. PUPPIES"
                    maxLength={20}
                    // biome-ignore lint/a11y/noAutofocus: intentional — step 2 is entered by user click
                    autoFocus
                  />
                </div>
                {error && <div className={styles.error}>{error}</div>}
              </div>

              <Button variant="dark" onClick={handleEnroll} disabled={loading || !promoCode.trim()}>
                {loading ? 'Activating...' : 'Activate my code'}
              </Button>
            </div>
          </div>
          <div className={sharedStyles.footer}>
            <StepProgress currentStep={2} totalSteps={2} hideLabel highlightCurrentOnly />
            <ModalActions>
              <Button variant="secondary" onClick={() => setStep(1)}>
                &larr; Back
              </Button>
            </ModalActions>
          </div>
          <div className={sharedStyles.support}>
            Have questions?{' '}
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Fi%20Rescue%20Program%20Enrollment%20Questions`}>
              Contact support
            </a>
          </div>
        </>
      )}
    </Modal>
  );
}
